import { Avatar, Button, Card, Flex, message, Spin, Typography } from 'antd';
import ActionHeader from '../../../components/actionHeader';
import Page from '../../../components/page';
import { Container } from './styles';
import { useNavigate, useParams } from 'react-router';
import { CoffeeOutlined, PlusOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Company, License, LicenseEntitlement } from '../../../types';
import { getLicenseForCompany } from '../../../utils/httpRequest';
import { mapLicense } from './utils';
import CreateLicenseModal from '../components/createLicenseModal';
import TrialLicenseCard from '../components/trialLicenseCard';
import LicenseCard from '../components/licenseCard';

const LicenseForClient = () => {
  const { id: companyID = '' } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [showCreateLicenseModal, setShowCreateLicenseModal] = useState(false);
  const currentEntitlement = useRef<LicenseEntitlement>(undefined);

  const getData = async () => {
    try {
      setLoading(true);
      const res = await getLicenseForCompany(companyID);
      setLicenses(res);
    } catch (err) {
      messageApi.error('Get License Failed!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyID) {
      getData();
    }
  }, [companyID]);

  const mappedLicense = useMemo(() => mapLicense(licenses), [licenses]);

  const company = useMemo(
    () => ({
      companyID: licenses[0]?.companyID || companyID,
      companyName: licenses[0]?.companyName,
    }),
    [licenses]
  );

  const handleCreateTrailLicense = (entitlement: LicenseEntitlement) => {
    currentEntitlement.current = entitlement;
    setShowCreateLicenseModal(true);
  };

  return (
    <Page
      header={
        <ActionHeader
          title={`Licenses For ${licenses?.[0]?.companyName || 'Client'}`}
          showBack
          onBack={() => navigate(-1)}
        />
      }
    >
      {contextHolder}
      <Spin spinning={loading}>
        <Container gap={16}>
          {mappedLicense.map((e) =>
            e.licenses?.length ? (
              e.isTrial ? (
                <TrialLicenseCard key={e.id} {...e} onRefresh={getData} />
              ) : (
                <LicenseCard key={e.id} {...e} onRefresh={getData} />
              )
            ) : (
              <Card style={{ minWidth: 300 }} key={e.id}>
                <Card.Meta
                  avatar={
                    <Avatar
                      icon={<CoffeeOutlined />}
                      style={{
                        backgroundColor: 'transparent',
                        color: theme.antd.colorTextSecondary,
                        fontSize: 20,
                      }}
                    />
                  }
                  title={
                    <Flex>
                      <Flex flex={1} justify="space-between" align="center">
                        <Typography.Text type="secondary">
                          {e.title} - No License
                        </Typography.Text>
                        <Button
                          type="primary"
                          ghost
                          onClick={() => handleCreateTrailLicense(e.id)}
                        >
                          <PlusOutlined />
                          Create License
                        </Button>
                      </Flex>
                    </Flex>
                  }
                  description={<p>{e.description}</p>}
                />
              </Card>
            )
          )}
        </Container>
      </Spin>
      <CreateLicenseModal
        open={showCreateLicenseModal}
        company={company as unknown as Company}
        entitlement={currentEntitlement.current}
        limitEndDate={mappedLicense[0].licenses?.[0]?.endDate}
        onClose={() => {
          setShowCreateLicenseModal(false);
        }}
        onConfirm={() => {
          setShowCreateLicenseModal(false);
          getData();
        }}
      />
    </Page>
  );
};

export default LicenseForClient;
